import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { CloseOutlined } from '@ant-design/icons';
import { Button, Input, Select } from 'antd';

import ModalActions from 'components/ModalActions';
import { Field, Formik } from 'formik';
import { constants } from 'helpers';
import { functions } from 'helpers/functions';
import UrlValidationFormField from 'v2/components/Shared/UrlValidationFormField';
import type { TestResult } from 'v2/interfaces/delivery-providers';
import { isUrl } from 'validations/rules';
import * as yup from 'yup';

import { PROVIDER_CONFIG, type IProps } from './types';
import { useDeliveryProvider } from './useDeliveryProvider';

const { APOLLO, CUSTOM, LEADLOVERS, EADPLATAFORMA, HOTMART, WEVE } = constants.PROVIDERS;

const PROVIDERS_WITH_URL = [CUSTOM.ID, WEVE.ID, APOLLO.ID, EADPLATAFORMA.ID, LEADLOVERS.ID];

const Other: React.FC<IProps> = ({ isModal, open, onClose, onOk, other, newProduct, hasHotmart, deliveries }) => {
  const [selected, setSelected] = useState<any>('');
  const [inputSelected, setInputSelected] = useState<number>(0);
  const [parameters, setParameters] = useState<any>(null);
  const [fields, setFields] = useState<any>({});
  const [defaultFields, setDefaultFields] = useState<any>({});
  const [pinParameters, setPinParameters] = useState<any>(null);
  const [isCustomProviderUrlValid, setIsCustomProviderUrlValid] = useState(false);
  const { data: others } = useDeliveryProvider(newProduct);

  useEffect(() => {
    if (!other) return;

    const methodId = Number(other.method_id);
    const selectedOther = others?.find(item => item.id === methodId);

    setSelected(other);
    setInputSelected(methodId);
    setParameters(selectedOther?.parameters ?? null);
    setFields(other);
    setDefaultFields({ ...other });
    setIsCustomProviderUrlValid(PROVIDERS_WITH_URL.includes(methodId) && !!other.url);
  }, [other, others]);

  const handleChange = useCallback(
    (id: number) => {
      if (!others?.length) return;
      const otherDelivery = others.find(i => i.id === id);
      const newSelected: any = { ...otherDelivery };
      newSelected.method_id = `${newSelected.id}`;
      newSelected.method_name = newSelected.name;
      setSelected(newSelected);
      setParameters(newSelected.parameters);
      setInputSelected(id);
      setFields(newSelected);
      setIsCustomProviderUrlValid(false);
    },
    [others]
  );

  const onChangeProp = useCallback((propName: any, value: any) => {
    setFields((prev: any) => ({ ...prev, [propName]: value }));
  }, []);

  const handleClose = useCallback(() => {
    if (parameters) parameters.map((p: any) => onChangeProp(p.name, defaultFields[p.name]));
    onClose();
  }, [parameters, defaultFields, onChangeProp, onClose]);

  const handleOk = useCallback(() => {
    let newFields = { ...fields };
    let msg = '';
    let isValid = true;

    if (fields?.id === 9) {
      newFields = Object.assign(fields, pinParameters);

      if (fields) {
        isValid = false;
        onClose();
      }
    } else {
      for (const prop in parameters) {
        const propName = parameters[prop].name;
        const label = parameters[prop].label;

        if (fields[propName] && fields[propName] !== '' && isValid) {
          if (propName.includes('url')) {
            if (isUrl(fields[propName])) {
              isValid = false;
              msg = `Por favor informe uma Url válida no campo ${label}`;
            }
          }
        } else {
          isValid = false;
          if (!msg) msg = `Por favor preencha o campo ${label}`;
          break;
        }
      }
    }

    if (fields?.id) {
      if (isValid) {
        if (onOk) {
          if (functions.isEqual(fields, selected)) return onClose();
          newFields.new = true;
          newFields.can_modify = true;
          const alreadyExistsUrl = deliveries?.findIndex((d: any) => d.url === fields.url) !== -1;

          if (alreadyExistsUrl) {
            toast.warn('URL já adicionada');
            return;
          }

          setFields(newFields);
          onOk(newFields, defaultFields);
          if (!other) toast.success(`Item ${fields.name} adicionado com sucesso!`);
          setSelected({});
          setParameters(null);
          setPinParameters(null);
          setInputSelected(0);
        }
      } else {
        toast.warn(`${msg}`);
      }
    } else {
      toast.warn('Por favor selecione uma forma de entrega do tipo outros');
    }
  }, [fields, parameters, pinParameters, selected, defaultFields, deliveries, onOk, onClose, other]);

  const isOkButtonDisabled = useCallback(() => {
    return PROVIDERS_WITH_URL.includes(inputSelected) ? !isCustomProviderUrlValid : false;
  }, [inputSelected, isCustomProviderUrlValid]);

  const urlForm = useCallback(
    (
      paramName: string,
      validateUrlCallback: (url: string) => Promise<TestResult>,
      initialValue: string = '',
      label: string = '',
      isCustomDelivery: boolean
    ) => {
      const linkStyles = { color: 'inherit', fontWeight: 'bold' as const };
      return (
        <Formik
          key={paramName}
          initialValues={{ url: initialValue, type: 'invoice' }}
          validationSchema={yup.object().shape({
            url: yup
              .string()
              .required('Preencha o campo URL, obrigatório http:// ou https://')
              .url(`Insira uma URL ${label} válida, obrigatório http:// ou https://`)
          })}
          onSubmit={values => alert(JSON.stringify(values, null, 2))}
        >
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field
              name='url'
              helperText={`Insira uma URL ${label} válida, obrigatório http:// ou https://`}
              component={UrlValidationFormField}
              onValidateUrl={(valid: boolean, url: string) => {
                onChangeProp(paramName, url);
                setIsCustomProviderUrlValid(valid);
              }}
              validateUrlCallback={validateUrlCallback}
              helpLink={constants.links.HelpLink}
            />
            {isCustomDelivery && (
              <span style={{ fontSize: 12 }}>
                Para saber como funciona a entrega customizada, acesse a{' '}
                <a href={constants.links.HelpLink} style={linkStyles} target='_blank' rel='noreferrer'>
                  documentação técnica
                </a>{' '}
                ou confira nossa{' '}
                <a href={constants.links.CustomizedDeliveryFAQ} style={linkStyles} target='_blank' rel='noreferrer'>
                  FAQ
                </a>
              </span>
            )}
          </div>
        </Formik>
      );
    },
    [onChangeProp]
  );

  const filteredOthers = Array.isArray(others)
    ? others.filter(o => {
        if (o.id === HOTMART.ID && !hasHotmart) return false;
        if (o.id === 47) return false;
        return true;
      })
    : [];

  const content = (
    <form>
      <div style={{ width: '100%' }}>
        <label className='mb-1 block text-xs'>Selecione um outro tipo de entrega</label>
        <Select
          className='w-full'
          value={inputSelected || undefined}
          onChange={handleChange}
          options={filteredOthers.map((item: any) => ({ label: item.name, value: item.id }))}
        />
        {parameters?.map((param: any) => {
          const isUrlParam = param.name?.includes('url');
          const providerConfig = PROVIDER_CONFIG[inputSelected];
          if (isUrlParam && providerConfig)
            return urlForm(
              param.name,
              providerConfig.testUrl,
              fields[param.name] ?? '',
              `${providerConfig.preposition} ${fields.name}`,
              inputSelected === CUSTOM.ID
            );
          return (
            <React.Fragment key={param.name}>
              <div className='mt-4'>
                <label className='mb-1 block text-sm'>{param.label}</label>
                <Input
                  id={param.name}
                  onChange={(e: any) => onChangeProp(param.name, e.target.value)}
                  value={fields[param.name]}
                />
              </div>
              {param?.hint && <span className='text-xs text-gray-500'>{param.hint}</span>}
            </React.Fragment>
          );
        })}
      </div>
    </form>
  );

  return (
    <div>
      {isModal ? (
        <ModalActions
          buttonOk='Salvar'
          title='Entrega de Conteúdo'
          open={open}
          onClose={handleClose}
          onOk={handleOk}
          buttonOkDisabled={isOkButtonDisabled()}
          maskClosable={false}
        >
          {content}
        </ModalActions>
      ) : (
        <div className='rounded-sm bg-white shadow-sm'>
          <div className='flex items-center justify-between border-b p-4'>
            <h3 className='font-medium'>Entrega de Conteúdo</h3>
            <Button type='text' icon={<CloseOutlined />} />
          </div>
          <div className='p-4'>{content}</div>
        </div>
      )}
    </div>
  );
};

export default Other;
